import { THEME_AND_SETTINGS_FETCHED, THEME_SETTINGS_VARIANT_FETCHED, THEME_SETTINGS_BUFFER_PUBLISHED, THEME_SETTINGS_BUFFER_SAVED, THEME_SETTINGS_CHANGE_REDONE, THEME_SETTINGS_CHANGE_UNDONE, THEME_SETTINGS_FETCHED_HAS_BUFFERED_CHANGES, THEME_SETTINGS_FIELD_KEY_UPDATED, THEME_SETTINGS_REVERTED, THEME_SETTINGS_REVISIONS_FETCHED, THEME_SETTINGS_UPDATED, THEME_SETTINGS_RESET, THEME_SETTINGS_CLEARED, FETCH_THEME_SUCCEEDED, CLEAR_THEME_SETTINGS_REVISIONS, MODULE_REDUCER_CHANGE_UNDO, MODULE_REDUCER_CHANGE_REDO, SET_UNIVERSAL_SCROLL_TO_FIELD_PATH } from './ActionTypes';
import * as themesClient from '../api/ThemesClient';
import * as themeSettingsClient from '../api/ThemeSettingsClient';
import * as PresetClient from '../api/PresetClient';
import * as settingsSelectors from '../selectors/themeSettingsSelectors';
import { getIsUngatedForCHIRP } from '../selectors/authSelectors';
import { getTestMode } from '../selectors/appSelectors';
import { getActiveThemePresetObjectId, getHasThemePresetsAccess, getPublishedActiveThemePresetId, getActiveThemePresetType } from '../selectors/presetsSelectors';
import { getHasUnsavedPresetChanges } from '../selectors/themeSettingsMetaSelectors';
import { getThemePath } from '../selectors/themeSelectors';
import * as RequestStatusTypes from '../constants/RequestStatusTypes';
import { markActionComplete } from 'user-context/onboarding';
import { maybeMarkCommerceTaskComplete } from 'commerce-tools-ui-lib/public/utils';
import { CUSTOMIZE_QUOTE_THEME_STYLES } from 'commerce-tools-ui-lib/public/constants';
import { track } from '../utils/standaloneUsageTracker';
import { EVENTS as StandaloneTrackingEventTypes } from '../constants/usage/standaloneTracking';
import { convertChirpFieldValueObjectToJson } from '../utils/chirp';
const fetchThemeAndSettingsRequested = () => ({
  type: THEME_AND_SETTINGS_FETCHED,
  requestStatus: RequestStatusTypes.PENDING
});
const fetchThemeAndSettingsSucceeded = payload => ({
  type: THEME_AND_SETTINGS_FETCHED,
  payload,
  requestStatus: RequestStatusTypes.SUCCEEDED
});
const fetchThemeAndSettingsFailed = error => ({
  type: THEME_AND_SETTINGS_FETCHED,
  error: true,
  payload: error,
  requestStatus: RequestStatusTypes.FAILED
});
export const fetchThemeAndSettings = (themePath, themeVariant) => {
  return (dispatch, getState) => {
    const state = getState();
    dispatch(fetchThemeAndSettingsRequested());
    const testMode = getTestMode(state);
    // TODO: Handle test_mode variant using generic themeVariant query param
    const _themeVariant = testMode ? 'test_mode' : themeVariant;
    return themesClient.fetchThemeAndSettings(themePath, _themeVariant).then(response => {
      const hasThemePresetsAccess = getHasThemePresetsAccess(state, {
        themePath
      });
      if (hasThemePresetsAccess && !response.bufferedActiveThemePreset) {
        return PresetClient.initializeActivePreset(themePath).then(initializeResponse => {
          const responseWithBufferedActiveThemePreset = Object.assign({}, response, {
            bufferedActiveThemePreset: initializeResponse
          });
          dispatch(fetchThemeAndSettingsSucceeded(responseWithBufferedActiveThemePreset));
          return responseWithBufferedActiveThemePreset;
        }, error => {
          if (error.status) dispatch(fetchThemeAndSettingsFailed(error));
          throw error;
        });
      }
      dispatch(fetchThemeAndSettingsSucceeded(response));
      return response;
    }, error => {
      if (error.status) dispatch(fetchThemeAndSettingsFailed(error));
      throw error;
    });
  };
};
const themeSettingsVariantRequested = () => ({
  type: THEME_SETTINGS_VARIANT_FETCHED,
  requestStatus: RequestStatusTypes.PENDING
});
const themeSettingsVariantReceived = payload => ({
  type: THEME_SETTINGS_VARIANT_FETCHED,
  payload,
  requestStatus: RequestStatusTypes.SUCCEEDED
});
const themeSettingsVariantFetchFailed = error => ({
  type: THEME_SETTINGS_VARIANT_FETCHED,
  error: true,
  payload: error,
  requestStatus: RequestStatusTypes.FAILED
});
export const fetchThemeSettingsVariant = (themePath, variantName) => {
  return dispatch => {
    dispatch(themeSettingsVariantRequested());
    return themeSettingsClient.fetchThemeVariant(themePath, variantName).then(result => {
      dispatch(themeSettingsVariantReceived(result));
      return result;
    }, error => {
      if (error.status) dispatch(themeSettingsVariantFetchFailed(error));
      throw error;
    });
  };
};
const saveSettingsBufferRequested = () => ({
  type: THEME_SETTINGS_BUFFER_SAVED,
  requestStatus: RequestStatusTypes.PENDING
});
const saveSettingsBufferSucceeded = payload => ({
  type: THEME_SETTINGS_BUFFER_SAVED,
  payload,
  requestStatus: RequestStatusTypes.SUCCEEDED
});
const saveSettingsBufferFailed = error => ({
  type: THEME_SETTINGS_BUFFER_SAVED,
  error: true,
  payload: error,
  requestStatus: RequestStatusTypes.FAILED
});
export const saveSettingsBuffer = ({
  isAutoSave,
  fromUniversal = false
}) => {
  return (dispatch, getState) => {
    const state = getState();
    const settingsId = settingsSelectors.getThemeSettingsId(state);
    const values = settingsSelectors.getThemeSettingsValues(state);
    const breakpointStyles = settingsSelectors.getThemeBreakpointStyles(state);
    dispatch(saveSettingsBufferRequested());
    const promises = [themeSettingsClient.saveBuffer(settingsId, {
      breakpointStyles,
      values
    })];
    const hasThemePresetsAccess = getHasThemePresetsAccess(state);
    const hasUnsavedPresetChanges = getHasUnsavedPresetChanges(state);
    const activeThemePresetObjectId = getActiveThemePresetObjectId(getState());
    if (hasThemePresetsAccess && hasUnsavedPresetChanges && activeThemePresetObjectId) {
      const activeThemePresetId = settingsSelectors.getActiveThemePresetId(state);
      const activeThemePresetType = getActiveThemePresetType(state);
      promises.push(PresetClient.updateActivePresetBuffer(activeThemePresetObjectId, activeThemePresetType, activeThemePresetId));
    }
    return Promise.all(promises).then(([themeSettingsPayload]) => {
      dispatch(saveSettingsBufferSucceeded(Object.assign({}, themeSettingsPayload, {
        isAutoSave,
        fromUniversal
      })));
    }).catch(error => {
      if (error.status) dispatch(saveSettingsBufferFailed(error));
      throw error;
    });
  };
};
const publishSettingsBufferRequested = () => ({
  type: THEME_SETTINGS_BUFFER_PUBLISHED,
  requestStatus: RequestStatusTypes.PENDING
});
const publishSettingsBufferSucceeded = payload => ({
  type: THEME_SETTINGS_BUFFER_PUBLISHED,
  payload,
  requestStatus: RequestStatusTypes.SUCCEEDED
});
const publishSettingsBufferFailed = error => ({
  type: THEME_SETTINGS_BUFFER_PUBLISHED,
  error: true,
  payload: error,
  requestStatus: RequestStatusTypes.FAILED
});
export const publishSettingsBuffer = () => {
  return (dispatch, getState) => {
    const state = getState();
    const settingsId = settingsSelectors.getThemeSettingsId(state);
    const isUngatedForCHIRP = getIsUngatedForCHIRP(state);
    const hasThemePresetsAccess = getHasThemePresetsAccess(state);
    const themePath = getThemePath(state);
    const activeThemePresetObjectId = getActiveThemePresetObjectId(state);
    const activeThemePresetId = settingsSelectors.getActiveThemePresetId(state);
    dispatch(publishSettingsBufferRequested());
    const useCombinedPublish = hasThemePresetsAccess && activeThemePresetObjectId;
    const publishPromise = useCombinedPublish ? themeSettingsClient.publishCombinedBuffer(settingsId, themePath) : themeSettingsClient.publishBuffer(settingsId);
    publishPromise.then(response => {
      let payload = response;
      let publishEventOptions = {};
      if (useCombinedPublish) {
        const combinedResponse = response;
        const newActiveThemePresetObjectId = combinedResponse.activeThemePreset.id;
        const themeSettingsObject = isUngatedForCHIRP ? combinedResponse.themeSettings : combinedResponse.themeSettingsValues;
        payload = Object.assign({}, themeSettingsObject, {
          activeThemePresetObjectId: newActiveThemePresetObjectId,
          publishedActiveThemePresetId: activeThemePresetId
        });
        const publishedActiveThemePresetId = getPublishedActiveThemePresetId(state);
        publishEventOptions = {
          changedPreset: activeThemePresetId !== publishedActiveThemePresetId
        };
      }
      if (isUngatedForCHIRP && payload) {
        payload.values = convertChirpFieldValueObjectToJson(payload.values);
        payload.breakpointStyles = convertChirpFieldValueObjectToJson(payload.breakpointStyles);
      }
      dispatch(publishSettingsBufferSucceeded(payload));
      track(StandaloneTrackingEventTypes.PUBLISH_SETTINGS, publishEventOptions);
      markActionComplete('select-style-v2').catch(err => {
        setTimeout(() => {
          throw err;
        });
      });

      // Only marks task complete if user is in CPQ onboarding flow with a commerce theme
      // see https://git.hubteam.com/HubSpot/commerce-tools-ui-lib/blob/master/commerce-tools-ui-lib/static/js/utils/urls.ts#L29-L30
      maybeMarkCommerceTaskComplete({
        taskKey: CUSTOMIZE_QUOTE_THEME_STYLES
      });
    }).catch(error => {
      if (error.status) {
        dispatch(publishSettingsBufferFailed(error));
        return;
      }
      throw error;
    });
  };
};
const themeSettingsUpdated = ({
  breakpoint,
  key,
  themeSettings,
  value
}) => ({
  type: THEME_SETTINGS_UPDATED,
  payload: {
    breakpoint,
    key,
    themeSettings,
    value
  }
});
export const updateSettings = ({
  breakpoint,
  key,
  themeSettings,
  value
}) => {
  return dispatch => {
    dispatch(themeSettingsUpdated({
      breakpoint,
      key,
      themeSettings,
      value
    }));
  };
};
const redoChangeAction = () => ({
  type: THEME_SETTINGS_CHANGE_REDONE
});
export const redoChange = () => {
  return dispatch => {
    dispatch(redoChangeAction());
  };
};
const clearThemeSettingsAction = () => ({
  type: THEME_SETTINGS_CLEARED
});
export const clearThemeSettings = () => {
  return dispatch => {
    dispatch(clearThemeSettingsAction());
  };
};
const undoChangeAction = () => ({
  type: THEME_SETTINGS_CHANGE_UNDONE
});
export const undoChange = () => {
  return dispatch => {
    dispatch(undoChangeAction());
  };
};
const fetchHasBufferedSettingsChangesRequested = () => ({
  type: THEME_SETTINGS_FETCHED_HAS_BUFFERED_CHANGES,
  requestStatus: RequestStatusTypes.PENDING
});
const fetchHasBufferedSettingsChangesSucceeded = (hasChanges, changes) => ({
  type: THEME_SETTINGS_FETCHED_HAS_BUFFERED_CHANGES,
  payload: {
    hasChanges,
    changes
  },
  requestStatus: RequestStatusTypes.SUCCEEDED
});
const fetchHasBufferedSettingsChangesFailed = error => ({
  type: THEME_SETTINGS_FETCHED_HAS_BUFFERED_CHANGES,
  error: true,
  payload: error,
  requestStatus: RequestStatusTypes.FAILED
});
export const fetchHasBufferedSettingsChanges = () => {
  return (dispatch, getState) => {
    const state = getState();
    const settingsId = settingsSelectors.getThemeSettingsId(state);
    const settingsValues = settingsSelectors.getThemeSettingsValues(state);
    dispatch(fetchHasBufferedSettingsChangesRequested());
    const promises = [themeSettingsClient.hasBufferedChanges(settingsId)];
    const hasThemePresetsAccess = getHasThemePresetsAccess(state);
    const hasUnsavedPresetChanges = getHasUnsavedPresetChanges(state);
    const activeThemePresetObjectId = getActiveThemePresetObjectId(getState());
    if (hasThemePresetsAccess && hasUnsavedPresetChanges && activeThemePresetObjectId) {
      promises.push(PresetClient.hasBufferedPresetChanges(activeThemePresetObjectId));
    }
    Promise.all(promises).then(results => {
      const themeSettingsResult = results[0];
      const presetResult = results[1];
      const hasChanges = themeSettingsResult.has_changes || (presetResult === null || presetResult === void 0 ? void 0 : presetResult.hasChanges) || false;
      const changes = hasChanges ? settingsValues : null;
      dispatch(fetchHasBufferedSettingsChangesSucceeded(hasChanges, changes));
    }).catch(error => {
      if (error.status) {
        dispatch(fetchHasBufferedSettingsChangesFailed(error));
        return;
      }
      throw error;
    });
  };
};
export const updateSettingsFieldKey = fieldKey => {
  return {
    type: THEME_SETTINGS_FIELD_KEY_UPDATED,
    payload: fieldKey
  };
};
const fetchRevisionsRequested = () => ({
  type: THEME_SETTINGS_REVISIONS_FETCHED,
  requestStatus: RequestStatusTypes.PENDING
});
const fetchRevisionsSucceeded = payload => ({
  type: THEME_SETTINGS_REVISIONS_FETCHED,
  payload,
  requestStatus: RequestStatusTypes.SUCCEEDED
});
const fetchRevisionsFailed = error => ({
  type: THEME_SETTINGS_REVISIONS_FETCHED,
  error: true,
  payload: error,
  requestStatus: RequestStatusTypes.FAILED
});
export const fetchRevisions = offset => (dispatch, getState) => {
  const state = getState();
  const settingsId = settingsSelectors.getThemeSettingsId(state);
  dispatch(fetchRevisionsRequested());
  themeSettingsClient.fetchRevisions(settingsId, {
    offset
  }).then(payload => {
    //@ts-expect-error FIXME: this won't be needed when we get rid of old type before CHIRP
    dispatch(fetchRevisionsSucceeded(payload));
  }).catch(error => {
    if (error.status) {
      dispatch(fetchRevisionsFailed(error));
      return;
    }
    throw error;
  });
};
export const clearRevisions = () => ({
  type: CLEAR_THEME_SETTINGS_REVISIONS
});
const revertThemeSettingsRequested = () => ({
  type: THEME_SETTINGS_REVERTED,
  requestStatus: RequestStatusTypes.PENDING
});
const revertThemeSettingsSucceeded = () => ({
  type: THEME_SETTINGS_REVERTED,
  requestStatus: RequestStatusTypes.SUCCEEDED
});
const revertThemeSettingsFailed = error => ({
  type: THEME_SETTINGS_REVERTED,
  error: true,
  payload: error,
  requestStatus: RequestStatusTypes.FAILED
});
export const revertThemeSettings = versionId => (dispatch, getState) => {
  const state = getState();
  const settingsId = settingsSelectors.getThemeSettingsId(state);
  const themePath = getThemePath(state);
  const hasThemePresetsAccess = getHasThemePresetsAccess(state, {
    themePath
  });
  const activeThemePresetObjectId = getActiveThemePresetObjectId(state);
  dispatch(revertThemeSettingsRequested());
  const revertPromise = hasThemePresetsAccess && activeThemePresetObjectId ? themeSettingsClient.revertCombinedThemeSettings(settingsId, {
    versionId,
    themePath
  }) : themeSettingsClient.revertThemeSettings(settingsId, {
    versionId
  });
  revertPromise.then(() => {
    dispatch(revertThemeSettingsSucceeded());
  }).catch(error => {
    if (error.status) {
      dispatch(revertThemeSettingsFailed(error));
      return;
    }
    throw error;
  });
};
const themeSettingsReset = (key, breakpoint) => ({
  type: THEME_SETTINGS_RESET,
  payload: {
    breakpoint,
    key
  }
});

//used just for ts type so we can handle CEUI action
const fetchThemeSucceeded = (payload, response) => ({
  type: FETCH_THEME_SUCCEEDED,
  payload,
  response,
  requestStatus: RequestStatusTypes.SUCCEEDED
});
const undoUniversalThemeAction = payload => ({
  type: MODULE_REDUCER_CHANGE_UNDO,
  payload
});
const redoUniversalThemeAction = payload => ({
  type: MODULE_REDUCER_CHANGE_REDO,
  payload
});
export const resetFieldSettings = (key, breakpoint) => dispatch => {
  dispatch(themeSettingsReset(key, breakpoint));
};
export const setUniversalScrollToFieldPath = payload => ({
  type: SET_UNIVERSAL_SCROLL_TO_FIELD_PATH,
  payload
});